/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Filter } from '../../../model/Filter';
import { Command } from '../../../model/Command';
import { ListController } from '../../ListController';

class EquipmentTemplateListController extends ListController {
    /** @ngInject */
    constructor(equipmentTemplateService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService,
        configuration, domainService, preventiveSheetTemplateService, filterService, dialogService,
        $scope) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.domainService = domainService;
        this.equipmentTemplateService = equipmentTemplateService;
        this.preventiveSheetTemplateService = preventiveSheetTemplateService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$scope = $scope;
        // mdDataTable doesn't support ES6 syntax with $ctrl variable in templates. That's why we must attach the reload
        // callback to the scope with an arrow function so as it can be called by this component with the correct 'this'
        // binding.
        this.$scope.reload = () => this.reload();
        this.$log.debug('Controller \'EquipmentTemplateList\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.paging.sort = 'name';
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
            this.reload();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.domainFilter = Filter.equalFilterEmpty('domain.id', 'model.domain.domain', 'SELECT', [],
            domainId => this.getFilterPreventiveSheetTemplates(domainId));

        this.preventiveSheetTemplateFilter = Filter.equalFilterEmpty('preventiveSheetTemplate.id',
            'model.preventivesheettemplate.preventivesheettemplate', 'SELECT', []);

        this.filters.push(
            this.domainFilter,
            Filter.emptyLikeFilter('name', 'model.equipmenttemplate.name'),
            this.preventiveSheetTemplateFilter
        );
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        this.commands.push(
            Command.stateCommand('ui.equipmenttemplate.create', 'images/svg/add.svg',
                'gmao.equipment.template.create'),
            Command.handlerCommand(undefined, 'images/svg/reset.svg', () => this.reload())
        );
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains().then(() => this.getFilterPreventiveSheetTemplates(this.domainFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of preventive sheet templates for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of preventive sheet templates.
     */
    getFilterPreventiveSheetTemplates(domainId) {
        return this.filterService.getFilterOptions(this.preventiveSheetTemplateService,
            this.preventiveSheetTemplateFilter, 'name', [Filter.equalFilter('domain.id', domainId)]);
    }

    /**
     * Reloads the list of equipment templates.
     *
     * @return {Promise} A promise providing the list of equipment templates.
     */
    reload() {
        return this.handleRestServiceCall(
            this.equipmentTemplateService.searchEntities({
                params: this.filterService.buildPagingParams(this.paging),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.model.data = data.content;
                this.model.totalElements = data.totalElements;
            }
        );
    }

    /**
     * Shows a confirmation dialog before deleting an equipment template. If the dialog is confirmed, the template is
     * deleted.
     *
     * @param {integer} id Equipment template ID.
     * @return {Promise} A promise containing the confirmation result.
     */
    showDeleteConfirmationDialog(id) {
        return this.dialogService.showConfirmationDialogWithMessage(
            'ui.equipmenttemplate.deleteconfirmation'
        ).then(confirmed => {
            if (confirmed) {
                this.deleteEquipmentTemplate(id);
            }
            return confirmed;
        });
    }

    /**
     * Deletes immediately an equipment template without confirmation, and reloads the list of templates.
     *
     * @param {integer} id Equipment template ID.
     */
    deleteEquipmentTemplate(id) {
        return this.handleRestServiceCall(this.equipmentTemplateService.deleteEntity(id), () => {
            this.notificationService.pushMessage('ui.equipmenttemplate.deleted',
                this.configuration.NOTIFIER_LEVEL_INFO);
            this.reload();
        });
    }
}

export const equipmentTemplateListComponent = {
    template: require('./equipmenttemplate-list.html'),
    controller: EquipmentTemplateListController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/equipment/component/EquipmentTemplateList.js